import { Tooltip } from 'antd';
import type { ReactElement } from 'react';

import type { Permission } from '@/api/generated/enums';
import { blockedReason } from '@core/session/capability';
import type { Session } from '@core/session/types';

/**
 * A write action, with the two reasons it might not be available handled in one place.
 *
 * Every master screen has four or five of these, and doing it by hand per button is how one screen
 * ends up checking permission, its neighbour checking only the subscription, and a third offering
 * a button the API refuses. The rule the whole UI runs on — never offer a write the server will
 * reject — is only worth anything if it is applied uniformly.
 *
 * Disabled rather than hidden, with the reason on hover: a missing button teaches nothing, while a
 * disabled one that explains itself tells the user whether to call their administrator or their
 * accounts department. (Ant Design's Tooltip wraps disabled children itself, so the explanation
 * survives the pointer-events the disabled state drops.)
 */
export function WriteAction({
  session,
  required,
  children,
}: {
  session: Session;
  required: Permission;
  /** Receives whether the action must be disabled, so the caller keeps control of the control. */
  children: (disabled: boolean) => ReactElement;
}) {
  const reason = blockedReason(session, required);
  const element = children(reason !== undefined);

  return reason ? (
    <Tooltip title={reason} placement="left">
      {element}
    </Tooltip>
  ) : (
    element
  );
}

/**
 * The same, plus a rule belonging to the record rather than to the user — a closed financial year,
 * a system role, the tenant owner's own account. Kept distinct from {@link WriteAction} because
 * the wording has to name the record's rule when there is one: "you do not have permission" is a
 * false explanation for a row that nobody may edit.
 */
export function RecordAction({
  session,
  required,
  forbidden,
  children,
}: {
  session: Session;
  required: Permission;
  /** Why this particular record refuses the action, or undefined when it does not. */
  forbidden?: string | undefined;
  children: (disabled: boolean) => ReactElement;
}) {
  const reason = forbidden ?? blockedReason(session, required);
  const element = children(reason !== undefined);

  return reason ? (
    <Tooltip title={reason} placement="left">
      {element}
    </Tooltip>
  ) : (
    element
  );
}
